
import { Card } from "@/components/ui/card";
import { Check, Loader2 } from "lucide-react";
import type { SubscriptionTier } from "@/types/subscription";
import { useSubscriptionData } from "@/hooks/useSubscriptionData";

interface SubscriptionTierCardProps {
  tier: SubscriptionTier;
  isLoadingPayPal: boolean;
  scriptError: boolean;
  onRetry: () => void;
}

export const SubscriptionTierCard = ({
  tier,
  isLoadingPayPal,
  scriptError,
  onRetry,
}: SubscriptionTierCardProps) => {
  const { data: subscription } = useSubscriptionData();

  return (
    <Card 
      className={`relative overflow-hidden ${
        tier.highlighted 
          ? 'border-purple-500 shadow-lg ring-2 ring-purple-500 ring-opacity-50' 
          : 'hover:shadow-md transition-shadow'
      }`}
    >
      {tier.highlighted && (
        <div className="absolute top-0 right-0 bg-purple-500 text-white px-3 py-1 text-xs sm:px-4 sm:text-sm font-medium rounded-bl">
          Most Popular
        </div>
      )}
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <tier.icon className={`h-5 w-5 sm:h-6 sm:w-6 ${
              tier.highlighted ? 'text-purple-500' : 'text-gray-600'
            }`} />
            <h3 className="text-xl sm:text-2xl font-bold">{tier.name}</h3>
          </div>
          <p className="text-gray-600 text-xs sm:text-sm">{tier.description}</p>
        </div>
        
        <div className="space-y-1">
          <div className="text-2xl sm:text-3xl font-bold">{tier.price}</div>
          <div className="text-gray-600 text-xs sm:text-sm">per month</div>
        </div>

        <div className="space-y-2 sm:space-y-3">
          {tier.features.map((feature) => (
            <div key={feature} className="flex items-start gap-2">
              <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 shrink-0 mt-0.5" />
              <span className="text-gray-600 text-xs sm:text-sm">{feature}</span>
            </div>
          ))}
        </div>

        <div className="min-h-[48px]">
          {subscription?.status === 'active' ? (
            <button
              disabled
              className="w-full py-2 px-4 rounded-lg text-sm sm:text-base font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
            >
              Already Subscribed
            </button>
          ) : (
            <>
              <div 
                id={`paypal-button-${tier.paypalPlanId}`} 
                className={`${isLoadingPayPal || scriptError ? 'hidden' : ''}`}
              />
              {isLoadingPayPal && (
                <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading payment options...
                </div>
              )}
              {scriptError && (
                <button
                  onClick={onRetry}
                  className="w-full py-2 px-4 rounded-lg text-sm sm:text-base font-medium bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                >
                  Retry Loading Payment Options
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};
